/* ============================================================
   AFYO THEME — JavaScript
   ============================================================ */

'use strict';

// ─── CART STATE ─────────────────────────────────────────────

const AFYO = {
  cart: { items: [], item_count: 0, total_price: 0 },

  async fetchCart() {
    const res = await fetch('/cart.js');
    this.cart = await res.json();
    return this.cart;
  },

  async addToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    await this.fetchCart();
    this.updateCartUI();
    CartDrawer.open();
    return this.cart;
  },

  async updateCartItem(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    await this.fetchCart();
    this.updateCartUI();
  },

  updateCartUI() {
    const count = this.cart.item_count;
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    CartDrawer.render(this.cart);
  }
};

// ─── CART DRAWER ─────────────────────────────────────────────

const CartDrawer = {
  el: null,
  overlay: null,

  init() {
    this.el = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-overlay');
    if (!this.el) return;

    document.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
  },

  open() {
    this.el?.classList.add('open');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.el?.classList.remove('open');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  },

  render(cart) {
    const itemsEl = this.el?.querySelector('.cart-drawer__items');
    const footerEl = this.el?.querySelector('.cart-drawer__footer');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-title">Empty.</div>
          <p>Your bag is empty.<br>Time to fix that.</p>
          <a href="/collections/all" class="btn btn-dark" onclick="CartDrawer.close()">Shop Now</a>
        </div>`;
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    if (footerEl) footerEl.style.display = '';

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__img" src="${item.image}" alt="${item.title}" loading="lazy">
        <div>
          <div class="cart-item__title">${item.product_title}</div>
          <div class="cart-item__variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
          <div class="cart-item__qty">
            <button onclick="AFYO.updateCartItem('${item.key}', ${item.quantity - 1})">−</button>
            <span>${item.quantity}</span>
            <button onclick="AFYO.updateCartItem('${item.key}', ${item.quantity + 1})">+</button>
          </div>
        </div>
        <div class="cart-item__price">${formatMoney(item.line_price)}</div>
      </div>
    `).join('');

    const subtotalEl = this.el?.querySelector('.cart-drawer__subtotal-price');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
  }
};

// ─── CART TOAST (add-to-cart confirmation) ───────────────────

const CartToast = {
  el: null,
  timer: null,

  init() {
    this.el = document.getElementById('cart-toast');
  },

  resize(url, width) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  },

  show({ title, variantTitle, image, message = 'Added to cart', isError = false } = {}) {
    if (!this.el) return;
    clearTimeout(this.timer);

    this.el.innerHTML = isError
      ? `
        <div class="cart-toast__inner cart-toast__inner--error">
          <button type="button" class="cart-toast__close" aria-label="Close">✕</button>
          <div class="cart-toast__msg cart-toast__msg--error">${message}</div>
        </div>`
      : `
        <div class="cart-toast__inner">
          <button type="button" class="cart-toast__close" aria-label="Close">✕</button>
          <div class="cart-toast__body">
            ${image ? `<img class="cart-toast__img" src="${this.resize(image, 160)}" alt="${title || ''}">` : ''}
            <div class="cart-toast__text">
              <div class="cart-toast__msg">${message}</div>
              ${title ? `<div class="cart-toast__title">${title}</div>` : ''}
              ${variantTitle ? `<div class="cart-toast__variant">${variantTitle}</div>` : ''}
            </div>
          </div>
          <div class="cart-toast__actions">
            <button type="button" class="btn btn-dark btn-full cart-toast__view">View Cart</button>
            <button type="button" class="btn btn-secondary btn-full cart-toast__continue">Continue Shopping</button>
          </div>
        </div>`;

    this.el.classList.add('active');

    this.el.querySelector('.cart-toast__close')?.addEventListener('click', () => this.hide());
    this.el.querySelector('.cart-toast__continue')?.addEventListener('click', () => this.hide());
    this.el.querySelector('.cart-toast__view')?.addEventListener('click', () => {
      this.hide();
      CartDrawer.open();
    });

    if (!isError) {
      this.timer = setTimeout(() => this.hide(), 6000);
    }
  },

  hide() {
    this.el?.classList.remove('active');
  }
};

// ─── PRODUCT FORM (AJAX add-to-cart) ─────────────────────────

const ProductForm = {
  init() {
    document.querySelectorAll('.product-form').forEach(form => {
      form.addEventListener('submit', e => this.handleSubmit(e, form));
    });
  },

  async handleSubmit(e, form) {
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (!btn || btn.disabled) return; // guard against duplicate submissions

    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.description || data.message || 'Could not add item to cart.');
      }

      await AFYO.fetchCart();
      AFYO.updateCartUI();

      CartToast.show({
        title: data.product_title,
        variantTitle: data.variant_title && data.variant_title !== 'Default Title' ? data.variant_title : '',
        image: data.image,
        message: 'Added to cart'
      });
    } catch (err) {
      CartToast.show({
        message: err.message || 'Something went wrong. Please try again.',
        isError: true
      });
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }
};

// ─── MOBILE MENU ─────────────────────────────────────────────

const MobileMenu = {
  init() {
    const toggle = document.querySelector('.hamburger');
    const menu = document.querySelector('.mobile-menu');
    const close = document.querySelector('.mobile-menu__close');

    toggle?.addEventListener('click', () => menu?.classList.add('open'));
    close?.addEventListener('click', () => menu?.classList.remove('open'));

    // Expandable/collapsible submenus (e.g. Shop)
    document.querySelectorAll('.mobile-nav__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const group = trigger.closest('.mobile-nav__group');
        if (!group) return;
        const isOpen = group.classList.contains('open');
        group.classList.toggle('open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }
};

// ─── PRODUCT PAGE ─────────────────────────────────────────────

const ProductPage = {
  init() {
    if (!document.querySelector('.product-page')) return;

    this.initGallery();
    this.initSizes();
    this.initStickyATC();
  },

  initGallery() {
    const mainImg = document.querySelector('.product-page__main-img');
    const thumbs = document.querySelectorAll('.product-page__thumb');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        if (mainImg) {
          mainImg.src = thumb.dataset.src;
          mainImg.srcset = '';
        }
      });
    });
  },

  initSizes() {
    const sizeBtns = document.querySelectorAll('.size-btn');
    sizeBtns.forEach(btn => {
      if (btn.dataset.available === 'false') {
        btn.classList.add('unavailable');
        return;
      }
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedVariant = btn.dataset.variantId;
        this.updateATCState(btn.dataset.available !== 'false');
      });
    });

    // Auto-select first available
    const firstAvailable = document.querySelector('.size-btn:not(.unavailable)');
    firstAvailable?.click();
  },

  initStickyATC() {
    const info = document.querySelector('.product-page__info');
    if (info && window.innerWidth < 768) {
      const obs = new IntersectionObserver(([entry]) => {
        document.querySelector('.sticky-atc')?.style.setProperty(
          'display', entry.isIntersecting ? 'none' : 'flex'
        );
      }, { threshold: 0.1 });
      obs.observe(info);
    }
  },

  updateATCState(available) {
    const btn = document.querySelector('.product-page__atc .btn-primary');
    const stickyBtn = document.querySelector('.sticky-atc .btn-primary');
    if (!btn) return;
    if (available) {
      btn.textContent = 'Add to Bag';
      btn.disabled = false;
      if (stickyBtn) { stickyBtn.textContent = 'Add to Bag'; stickyBtn.disabled = false; }
    } else {
      btn.textContent = 'Sold Out';
      btn.disabled = true;
      if (stickyBtn) { stickyBtn.textContent = 'Sold Out'; stickyBtn.disabled = true; }
    }
  }

};

// ─── HELPERS ─────────────────────────────────────────────────

function formatMoney(cents) {
  return (cents / 100).toLocaleString('en-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0
  });
}

// ─── EMAIL FORM ───────────────────────────────────────────────

function initEmailCapture() {
  const form = document.querySelector('.email-capture__form');
  if (!form) return;

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const email = form.querySelector('input[type="email"]').value;
    const btn = form.querySelector('.email-capture__submit');
    btn.textContent = 'Done!';
    btn.disabled = true;
    // Shopify's built-in newsletter integration happens via /contact form
    form.submit();
  });
}

// ─── QUICK ADD ────────────────────────────────────────────────

document.addEventListener('click', async e => {
  const btn = e.target.closest('.product-card__quick-add');
  if (!btn) return;
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  btn.textContent = 'Adding...';
  try {
    await AFYO.addToCart(variantId);
    btn.textContent = 'Added!';
    setTimeout(() => { btn.textContent = 'Add to Bag'; }, 1500);
  } catch {
    btn.textContent = 'Try Again';
  }
});

// ─── CART ICON ────────────────────────────────────────────────

document.querySelectorAll('.js-open-cart').forEach(btn => {
  btn.addEventListener('click', e => {
    e.preventDefault();
    CartDrawer.open();
  });
});

// ─── NAV DROPDOWN ────────────────────────────────────────────

function initNavDropdowns() {
  document.querySelectorAll('.nav-dropdown').forEach(dropdown => {
    const trigger = dropdown.querySelector('.nav-dropdown__trigger');

    trigger.addEventListener('click', e => {
      e.stopPropagation();
      const isOpen = dropdown.classList.contains('open');
      // close all others first
      document.querySelectorAll('.nav-dropdown.open').forEach(d => {
        d.classList.remove('open');
        d.querySelector('.nav-dropdown__trigger').setAttribute('aria-expanded', 'false');
      });
      if (!isOpen) {
        dropdown.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // close on outside click
  document.addEventListener('click', () => {
    document.querySelectorAll('.nav-dropdown.open').forEach(d => {
      d.classList.remove('open');
      d.querySelector('.nav-dropdown__trigger').setAttribute('aria-expanded', 'false');
    });
  });

  // close on Escape
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.nav-dropdown.open').forEach(d => {
        d.classList.remove('open');
        d.querySelector('.nav-dropdown__trigger').setAttribute('aria-expanded', 'false');
      });
    }
  });
}

// ─── INIT ─────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  CartDrawer.init();
  CartToast.init();
  ProductForm.init();
  MobileMenu.init();
  ProductPage.init();
  initEmailCapture();
  initNavDropdowns();

  await AFYO.fetchCart();
  AFYO.updateCartUI();
});
